import { useEffect, useMemo, useRef, useState } from "react";
import { DEFAULT_PRINT_BED, PRINT_ORIENTATIONS } from "@/lib/printOrientation";
import { useSearchParams } from "react-router-dom";
import { Link } from "react-router-dom";
import { Upload, CheckCircle2, AlertTriangle, ArrowLeft, ArrowRight, Package, Ruler, Coins, Send, FileText, Copy, X as XIcon, Maximize2, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from "@/components/ui/sheet";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { api, apiError, API } from "@/lib/api";
import { toast } from "sonner";
import { StlViewer } from "@/components/StlViewer";
import { useCatalogModel } from "@/hooks/useCatalogModel";

// Small helper — mm to inches at std 25.4 conversion. Only used for display;
// all backend math stays in mm.
const mmToIn = (mm) => (mm || 0) / 25.4;

// Turn a decimal number of hours into "Xh Ym" for a friendlier read.
const fmtHours = (h) => {
  if (!h || h <= 0) return "—";
  const hh = Math.floor(h);
  const mm = Math.round((h - hh) * 60);
  if (hh === 0) return `${mm}m`;
  if (mm === 0) return `${hh}h`;
  return `${hh}h ${mm}m`;
};

/**
 * PrintsQuotePage — public workhorse page at /prints/quote.
 *
 * Flow:
 *   1. Drop a file → we inspect its size.
 *      • ≤200MB (default) → uploaded to /api/prints/upload; parsed by StlViewer;
 *        volume + bbox measured client-side.
 *      • >200MB → user is told to send via Dropbox. Quote form still opens so
 *        admin can price it once the file lands out-of-band.
 *   2. Choose scale + quantity → live client-side quote (matches server math).
 *   3. Fill contact + shipping → submit → server persists + admin reviews.
 */
export default function PrintsQuotePage() {
  const viewerRef = useRef(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const catalogId = searchParams.get("catalog");
  const [config, setConfig] = useState(null);
  const [file, setFile] = useState(null);          // File object
  const [uploaded, setUploaded] = useState(null);   // { file_ref, file_ext, file_name, file_size_bytes }
  const [oversize, setOversize] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [geometry, setGeometry] = useState(null);   // { volume_mm3, bbox_mm, format }

  // Scale is split into a permanent BASE and a transient JOG:
  //   • baseScale — the committed size (target-dim, fit-to-bed, auto-shrink,
  //     direct numeric entry, or a slider release). Always the actual "state".
  //   • jog — where the slider is currently held on the [-1, 1] axis. Snaps
  //     back to 0 the instant the user lets go: whatever they nudged gets
  //     baked into base. So the slider is a jog wheel, not a range control —
  //     users can pull left/right and let go over and over for precision.
  // Downstream math + viewer use effectiveScale.
  const [baseScale, setBaseScale] = useState(1.0);
  const [jog, setJog] = useState(0);
  // Reference-scale objects toggled on/off in the viewer. Users get instant
  // "is this the size of a soda can or the size of a bottle?" intuition.
  const [refs, setRefs] = useState({ quarter: false, can: true, bottle: false });

  // Bake anything the user has committed (target-dim, fit-to-bed, direct
  // numeric entry). Slider is force-reset here too so the jog wheel returns
  // to center as soon as base changes for ANY reason.
  // NO UPPER CLAMP — user can scale as large as they want. Warning UI +
  // manual-review flag handle everything past the bed. Lower floor exists
  // only to prevent divide-by-zero / NaN downstream.
  const commitBase = (newBase) => {
    if (!Number.isFinite(newBase) || newBase <= 0) return;
    const safe = Math.max(0.001, newBase);
    setBaseScale(parseFloat(safe.toFixed(4)));
    setJog(0);
  };
  const [quantity, setQuantity] = useState(1);
  const [notes, setNotes] = useState("");
  const [manualBbox, setManualBbox] = useState({ x: 50, y: 50, z: 50 });   // fallback for unsupported viewer formats
  const [manualVolume, setManualVolume] = useState("");                    // cm³ (only when preview unavailable)

  const [contact, setContact] = useState({ name: "", email: "", phone: "" });
  const [ship, setShip] = useState({ line1: "", line2: "", city: "", region: "", postal_code: "", country: "" });

  const [submitting, setSubmitting] = useState(false);
  const [submittedQuote, setSubmittedQuote] = useState(null);
  const [showContact, setShowContact] = useState(false);   // slide-out address drawer
  const [modelError, setModelError] = useState("");
  const catalog = useCatalogModel(catalogId, ({ file: model, uploaded: source, scale }) => {
    setFile(model);
    setUploaded(source);
    setGeometry(null);
    setModelError("");
    setOversize(false);
    setSubmittedQuote(null);
    commitBase(scale);
  });

  const removeModel = () => {
    viewerRef.current?.clear?.();
    setFile(null); setUploaded(null); setGeometry(null); setOversize(false); setModelError("");
    if (catalogId) setSearchParams({}, { replace: true });
  };

  // Viewer stage height — fills the left column on desktop (viewport minus
  // the 56px sticky header), a comfortable fixed size on mobile.
  const [stageHeight, setStageHeight] = useState(720);
  useEffect(() => {
    const update = () => {
      if (typeof window === "undefined") return;
      const isDesktop = window.matchMedia("(min-width: 1024px)").matches;
      setStageHeight(isDesktop ? Math.max(520, window.innerHeight - 56) : 560);
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  useEffect(() => { void (async () => {
    try {
      const r = await api.get("/prints/config");
      setConfig(r.data);
    } catch (err) {
      toast.error("Couldn't load quote config — try again in a moment.");
    }
  })(); }, []);

  // Auto-shrink used to kick in here when a model landed bigger than the
  // bed, but the product requirement is now the opposite: users need to
  // SEE the oversized model + the "manual review" warning. So we leave the
  // scale alone on load; the viewer's setScale/re-framing keeps the whole
  // model visible even when it sticks out through the bed walls.

  // ── Drop / choose ───────────────────────────────────────────────
  const onFile = async (f) => {
    if (!f) return;
    if (catalogId) setSearchParams({}, { replace: true });
    // A fast file drop must not disappear while printer settings are loading.
    let settings = config;
    if (!settings) {
      try {
        settings = (await api.get("/prints/config")).data;
        setConfig(settings);
      } catch {
        toast.error("Printer settings could not be loaded. Please select your file again.");
        return;
      }
    }
    const ext = (f.name.split(".").pop() || "").toLowerCase();
    if (!["stl", "obj"].includes(ext)) {
      toast.error(`Only STL or OBJ files are accepted (got .${ext})`);
      return;
    }
    const limitBytes = (settings.file_upload_limit_mb || 200) * 1024 * 1024;
    setFile(f);
    setUploaded(null);
    setGeometry(null);
    setModelError("");
    commitBase(1);
    setSubmittedQuote(null);

    if (f.size > limitBytes) {
      setOversize(true);
      setUploaded(null);
      setGeometry(null);
      toast.warning(`File is ${(f.size / 1024 / 1024).toFixed(1)} MB — over the ${settings.file_upload_limit_mb} MB limit. Send it via Dropbox, but you can still request a quote below.`);
      return;
    }
    setOversize(false);
    // Upload + preview in parallel
    setUploading(true);
    setUploadPct(0);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const res = await api.post("/prints/upload", fd, {
        onUploadProgress: (p) => {
          if (p.total) setUploadPct(Math.round((p.loaded / p.total) * 100));
        },
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUploaded(res.data);
      toast.success("File uploaded — measuring model…");
    } catch (err) {
      toast.error(apiError(err, "Upload failed"));
    } finally {
      setUploading(false);
    }
  };

  // ── Live quote math (mirror of backend, so both agree) ──────────
  const rawBbox = geometry?.bbox_mm || manualBbox;
  const effectiveVolume = geometry?.volume_mm3 || (parseFloat(manualVolume || "0") * 1000);  // cm³ → mm³

  // Reported bbox is in printer convention: x=width, y=depth, z=height.
  // (We no longer auto-rotate to fit the bed — see the pinned-orientation
  // note below — so the model is always priced/shown in its as-loaded form.)
  const bed = config?.bed_mm || DEFAULT_PRINT_BED;

  // Slider is NOT capped by the printer bed — users can freely push past
  // the envelope and the cursor-following warning takes over from there.
  // 3× per jog stroke is a comfortable single-drag range; because the
  // slider snaps back to center on release, chaining jogs can grow the
  // model without any hard ceiling.
  const maxJogUp = 3;

  // Shrink floor — always allow at least 10× smaller from current base, but
  // never below 5% of the raw file so quote math stays sane.
  const maxJogDown = useMemo(() => {
    if (!baseScale) return 10;
    const floorFromRaw = 0.05 / baseScale;
    return Math.max(10, floorFromRaw);
  }, [baseScale]);

  // Convert the [-1,1] jog position into a multiplier. Two linear pieces so
  // the visual middle of the slider is exactly "no change" (modifier = 1.0),
  // regardless of asymmetric room on either side.
  const jogModifier = jog >= 0
    ? 1 + jog * (maxJogUp - 1)
    : 1 - Math.abs(jog) * (1 - 1 / maxJogDown);
  const effectiveScale = baseScale * jogModifier;

  // Pricing is pinned to the model's CANONICAL (as-loaded / upright)
  // orientation so the quote can't be lowered by rotating the model to lay it
  // flatter (MSLA print time is driven by Z height, so a flatter orientation
  // would otherwise shave real money off the estimate). The object can still
  // be rotated freely for inspection via the viewer's orbit controls — that
  // rotation is purely visual and never feeds the price. Over-bed models are
  // handled softly downstream (cursor-follow warning + manual admin review).
  const orientation = PRINT_ORIENTATIONS[0];

  // Bbox that downstream math uses — the canonical, un-rotated bounding box.
  const effectiveBbox = useMemo(() => orientation.permute(rawBbox || { x: 0, y: 0, z: 0 }), [orientation, rawBbox]);

  // Push the chosen rotation to the viewer so the visual model matches
  // the numeric bbox everyone else is using.
  useEffect(() => {
    if (viewerRef.current?.setRotation && geometry?.volume_mm3) {
      viewerRef.current.setRotation(...orientation.rot);
    }
  }, [orientation.rot, geometry]);

  // Keep the viewer's mesh scale in lock-step with the current effective
  // scale so users see the model shrink/grow inside the printer volume as
  // they jog the slider or type target dims.
  useEffect(() => {
    if (viewerRef.current && geometry?.volume_mm3) {
      viewerRef.current.setScale(effectiveScale);
    }
  }, [effectiveScale, geometry]);

  // The commit-on-release path is more work than it looks: `onMouseUp` on
  // the slider element only fires if the cursor is STILL over the slider at
  // release. Users routinely drag past the ends and let go anywhere on the
  // page. Fix: on pointerdown, attach a one-shot document-level listener
  // that fires on the next pointerup/mouseup/touchend NO MATTER where the
  // cursor is. Refs are used so the listener always sees the freshest jog
  // + base values instead of a stale closure.
  const jogRef = useRef(0);
  const baseRef = useRef(1);
  useEffect(() => { jogRef.current = jog; }, [jog]);
  useEffect(() => { baseRef.current = baseScale; }, [baseScale]);

  const bakeIfDirty = () => {
    const j = jogRef.current;
    if (Math.abs(j) < 0.001) return;
    const b = baseRef.current;
    // Recompute modifier from the ref values so we don't rely on state that
    // may have already re-rendered before this fires. Slider is uncapped by
    // the bed — same 3× headroom as the render-side formula.
    const maxUp = 3;
    const maxDown = Math.max(10, 0.05 / (b || 1));
    const mod = j >= 0 ? 1 + j * (maxUp - 1) : 1 - Math.abs(j) * (1 - 1 / maxDown);
    commitBase(b * mod);
  };

  const beginJog = () => {
    // Belt and braces — fire on ANY of these because different browsers /
    // input devices emit different sequences (pointer events on modern,
    // mouse-only on some older webviews, touch on mobile).
    const events = ["pointerup", "mouseup", "touchend", "touchcancel"];
    const handler = () => {
      events.forEach((ev) => document.removeEventListener(ev, handler));
      bakeIfDirty();
    };
    events.forEach((ev) => document.addEventListener(ev, handler, { once: true }));
  };

  // Pick the setup tier whose max_dim_mm covers the current scaled model.
  const quote = useMemo(() => {
    if (!config || !effectiveVolume || effectiveVolume <= 0) return null;
    const mat = (config.materials || [])[0] || {};
    const scaledVol = effectiveVolume * (effectiveScale ** 3);
    const printedVol = scaledVol * (config.support_multiplier || 1.05);
    const cm3 = printedVol / 1000;
    const weight = cm3 * (mat.density_g_per_cm3 || 1.05);
    const resinPerUnit = weight * (mat.usd_per_g || 0.197);

    // Print time — MSLA is driven by height only. In the ChiTuBox / printer
    // convention that's the Z axis (print stacking direction).
    const scaledZ = (effectiveBbox?.z || 0) * effectiveScale;
    const layerH = config.layer_height_mm || 0.05;
    const secPerLayer = config.seconds_per_layer ?? 11.01;
    const bottomSec = config.bottom_layer_seconds ?? 240;
    const layerCount = scaledZ > 0 ? Math.ceil(scaledZ / layerH) : 0;
    const printSeconds = bottomSec + layerCount * secPerLayer;
    const printHours = printSeconds / 3600;
    const hourlyRate = config.hourly_rate_usd ?? 1;
    const timePerUnit = printHours * hourlyRate;

    // Curing & cleaning — post-processing labour, scales with print time.
    const curingRate = config.curing_rate_usd_per_hour ?? 0.45;
    const curingPerUnit = printHours * curingRate;

    const scaledMaxDim = Math.max(
      (effectiveBbox?.x || 0) * effectiveScale,
      (effectiveBbox?.y || 0) * effectiveScale,
      (effectiveBbox?.z || 0) * effectiveScale,
    );
    const materialTotal = resinPerUnit * quantity;
    const timeTotal = timePerUnit * quantity;
    const curingTotal = curingPerUnit * quantity;
    const total = materialTotal + timeTotal + curingTotal;

    return {
      scaled_mm3: scaledVol,
      printed_mm3: printedVol,
      scaled_max_dim: scaledMaxDim,
      weight_g: weight,
      resin_cost: resinPerUnit,           // per unit
      material_total: materialTotal,       // × qty
      // Print time
      print_hours: printHours,
      layer_count: layerCount,
      time_cost: timePerUnit,             // per unit
      time_total: timeTotal,               // × qty
      hourly_rate: hourlyRate,
      // Curing & cleaning
      curing_rate: curingRate,
      curing_cost: curingPerUnit,          // per unit
      curing_total: curingTotal,           // × qty
      // Totals
      unit_price: total / quantity,        // blended
      quantity,
      total,
      material_label: mat.label || "Elegoo Standard Resin",
    };
  }, [config, effectiveVolume, effectiveBbox, effectiveScale, quantity]);

  // Compute which axes are over the printer bed. Over-bed no longer HARD-
  // blocks the quote — instead it flips on a "manual review required" flag
  // in the payload so admin knows to check whether the print can be done
  // (split, sliced, or on a bigger machine). Copy shifts to the reassuring
  // "we work diligently to get your prints perfect" tone.
  const bedIssue = useMemo(() => {
    if (!config || !effectiveBbox || oversize) return null;
    const bed = config.bed_mm || {};
    const over = [];
    for (const dim of ["x", "y", "z"]) {
      const s = (effectiveBbox[dim] || 0) * effectiveScale;
      if (s > (bed[dim] || 0)) {
        over.push(`${dim.toUpperCase()} = ${s.toFixed(1)}mm (bed ${bed[dim]}mm)`);
      }
    }
    return over.length ? over.join(" · ") : null;
  }, [config, effectiveBbox, effectiveScale, oversize]);

  // ── Cursor-following bed warning ────────────────────────────────
  // When the model exceeds the printer envelope, users need an in-your-face
  // signal — a static banner is easy to miss while they're focused on the
  // slider or dim inputs. We track the mouse pointer over the whole quote
  // page and render a floating amber pill 18px to the right/below the
  // cursor for as long as `bedIssue` stays truthy.
  const needsManualReview = !!bedIssue;
  const [cursor, setCursor] = useState({ x: -1000, y: -1000, visible: false });
  useEffect(() => {
    // Only listen while over-bed — no need to spam mousemove listeners in
    // the safe path.
    if (!bedIssue) return;
    const onMove = (e) => setCursor({ x: e.clientX, y: e.clientY, visible: true });
    const onLeave = () => setCursor((c) => ({ ...c, visible: false }));
    window.addEventListener("mousemove", onMove);
    document.addEventListener("mouseleave", onLeave);
    return () => {
      window.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseleave", onLeave);
    };
  }, [bedIssue]);

  // ── Submit ─────────────────────────────────────────────────────
  // Over-bed no longer disables submit; the quote just gets flagged for
  // admin review.
  const canSubmit =
    !catalog.loading && !catalog.error && !modelError &&
    file &&
    (uploaded || oversize) &&
    (geometry?.volume_mm3 > 0 || (parseFloat(manualVolume || "0") > 0)) &&
    contact.name.trim() &&
    /.+@.+\..+/.test(contact.email) &&
    contact.phone.trim() &&
    ship.line1.trim() && ship.city.trim() && ship.region.trim() && ship.postal_code.trim() && ship.country.trim() &&
    !submitting;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const payload = {
        file_name: uploaded?.file_name || file.name,
        file_ext: uploaded?.file_ext || ("." + (file.name.split(".").pop() || "").toLowerCase()),
        file_size_bytes: file.size,
        file_ref: uploaded?.file_ref || null,
        file_is_oversize: oversize,
        raw_volume_mm3: geometry?.volume_mm3 || (parseFloat(manualVolume) * 1000),
        bbox_mm: effectiveBbox,
        scale: effectiveScale,
        material_id: "elegoo_standard_resin",
        quantity,
        notes: notes.trim() || null,
        needs_manual_review: needsManualReview,
        contact_name: contact.name.trim(),
        contact_email: contact.email.trim(),
        contact_phone: contact.phone.trim(),
        shipping: {
          line1: ship.line1.trim(),
          line2: ship.line2.trim() || null,
          city: ship.city.trim(),
          region: ship.region.trim(),
          postal_code: ship.postal_code.trim(),
          country: ship.country.trim(),
        },
      };
      const res = await api.post("/prints/quote", payload);
      setSubmittedQuote(res.data);
      toast.success("Quote submitted — admin will review shortly.");
    } catch (err) {
      toast.error(apiError(err, "Couldn't submit quote"));
    } finally {
      setSubmitting(false);
    }
  };

  // ── After submit: confirmation card ────────────────────────────
  if (submittedQuote) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
        <div data-testid="quote-submitted-card" className="rounded-2xl border border-emerald-300/30 bg-emerald-400/5 p-8">
          <div className="flex items-center gap-3">
            <CheckCircle2 className="h-6 w-6 text-emerald-300" />
            <div>
              <div className="font-display text-2xl tracking-[0.06em] text-white">QUOTE #{submittedQuote.id.slice(0, 8).toUpperCase()} · Pending</div>
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-emerald-300/80 mt-0.5">Submitted for admin review</div>
            </div>
          </div>
          <div className="mt-6 grid sm:grid-cols-3 gap-3">
            <Metric label="Estimate" value={`$${submittedQuote.quote.total_usd.toFixed(2)}`} accent="#34d399" testid="quote-total" />
            <Metric label="Print weight" value={`${submittedQuote.quote.weight_g.toFixed(1)} g`} accent="#f472b6" />
            <Metric label="Quantity" value={submittedQuote.quantity} accent="#22d3ee" />
          </div>
          <p className="mt-6 text-sm text-white/60 leading-relaxed">
            {config?.disclaimer}
          </p>
          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <Button
              data-testid="quote-copy-id"
              variant="outline"
              className="border-white/15 text-white hover:bg-white/5"
              onClick={() => { navigator.clipboard.writeText(submittedQuote.id); toast.success("Quote ID copied."); }}
            >
              <Copy className="h-4 w-4 mr-1.5" /> Copy Quote ID
            </Button>
            <Link to="/prints">
              <Button className="bg-fuchsia-400 text-black hover:bg-fuchsia-300">
                <ArrowLeft className="h-4 w-4 mr-1.5" /> Back to 3D Printing
              </Button>
            </Link>
          </div>
          {oversize && (
            <div className="mt-6 rounded-xl border border-amber-400/40 bg-amber-400/10 p-4 text-sm text-amber-100">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                <div>
                  <div className="font-medium">Your file was too large to upload here.</div>
                  <div className="mt-1 text-amber-100/80">Please send the file to <span className="font-mono text-white">admin@playden.studio</span> via a Dropbox link, and include this Quote ID in the email so we can match them up.</div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── Composer ──────────────────────────────────────────────────
  // Workshop layout: full-viewport viewer with overlays on the LEFT,
  // sticky control rail on the RIGHT. Contact + shipping deferred to a
  // slide-out sheet so the primary screen is scroll-free on desktop.
  const scaledDims = geometry?.bbox_mm && {
    x: geometry.bbox_mm.x * effectiveScale,
    y: geometry.bbox_mm.y * effectiveScale,
    z: geometry.bbox_mm.z * effectiveScale,
  };
  return (
    <div className="min-h-screen bg-[hsl(222_22%_4%)] text-white" data-testid="prints-quote-page">
      {/* Cursor-following bed warning */}
      {bedIssue && cursor.visible && (
        <div
          data-testid="quote-cursor-warning"
          className="fixed z-[100] pointer-events-none max-w-xs rounded-xl border border-amber-400/60 bg-amber-500/95 shadow-[0_10px_40px_rgba(0,0,0,0.55)] px-3 py-2 text-[11px] leading-relaxed text-black"
          style={{
            left: Math.min(cursor.x + 18, window.innerWidth - 340),
            top: Math.min(cursor.y + 18, window.innerHeight - 140),
          }}
        >
          <div className="flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
            <div>
              <div className="font-mono uppercase tracking-[0.18em] text-[10px] font-bold">Larger than the printer envelope</div>
              <div className="mt-1 font-mono text-[10px] opacity-80">{bedIssue}</div>
              <div className="mt-1.5">You can still submit — this will be reviewed manually.</div>
            </div>
          </div>
        </div>
      )}

      {/* Compact top bar — one line */}
      <header data-testid="quote-workshop-header" className="relative z-20 border-b border-white/5 bg-black/60 backdrop-blur-md px-4 sm:px-6 h-14 flex items-center justify-between">
        <div className="flex items-center gap-3 min-w-0">
          <div className="h-8 w-8 rounded-lg bg-fuchsia-400/15 border border-fuchsia-300/40 flex items-center justify-center shrink-0">
            <Package className="h-4 w-4 text-fuchsia-300" />
          </div>
          <div className="min-w-0">
            <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-fuchsia-300/80 truncate">Playden 3D Printing</div>
            <div className="font-display text-sm tracking-[0.06em] text-white truncate">INSTANT QUOTE</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {file && (
            <div className="hidden sm:flex items-center gap-2 rounded-full border border-white/10 bg-white/5 pl-2.5 pr-1 py-1">
              <FileText className="h-3.5 w-3.5 text-white/60" />
              <span className="font-mono text-[11px] text-white/80 max-w-[240px] truncate">{file.name}</span>
              <button
                type="button"
                data-testid="quote-file-replace"
                onClick={removeModel}
                className="h-6 w-6 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white/70"
                title="Remove file"
              >
                <XIcon className="h-3 w-3" />
              </button>
            </div>
          )}
          <Link data-testid="quote-back-link" to={catalogId ? "/prints/shop" : "/prints"}>
            <Button data-testid="quote-back-button" variant="ghost" size="sm" className="text-white/70 hover:text-white h-8">
              <ArrowLeft className="h-3.5 w-3.5 mr-1.5" /> Back
            </Button>
          </Link>
        </div>
      </header>

      {/* Two-column workshop layout */}
      <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_480px] lg:h-[calc(100vh-3.5rem)]">
        {/* LEFT — viewer stage */}
        <div className="relative bg-[hsl(222_22%_5%)] border-r border-white/5">
          {catalog.loading || catalog.error ? (
            <div data-testid="quote-catalog-state" role="status" className="min-h-[420px] flex flex-col items-center justify-center gap-4 p-6 text-center">
              <p data-testid="quote-catalog-message" className={catalog.error ? "text-amber-200" : "text-white/70"}>{catalog.error || "Loading catalog model…"}</p>
              {catalog.error && <div className="flex flex-wrap justify-center gap-3">
                <Button data-testid="quote-catalog-retry" onClick={catalog.retry}>Retry model</Button>
                <Button data-testid="quote-catalog-upload-own" variant="outline" onClick={removeModel}>Choose my own file</Button>
              </div>}
            </div>
          ) : !file ? (
            <FullscreenDropzone
              limitMb={config?.file_upload_limit_mb || 200}
              onFile={onFile}
            />
          ) : (
            <>
              <StlViewer
                ref={viewerRef}
                modelFile={file}
                height={stageHeight}
                bedMm={config?.bed_mm || { x: 218, y: 122, z: 200 }}
                references={refs}
                onLoaded={(report) => setGeometry(report)}
                onError={(msg) => { setGeometry(null); setModelError(msg); toast.error(msg); }}
              />
              {modelError && <div role="alert" data-testid="quote-model-error" className="absolute inset-x-4 top-40 rounded-lg border border-amber-400/40 bg-black/90 p-4 text-sm text-amber-200">
                {modelError}<Button data-testid="quote-model-error-replace" variant="outline" className="mt-3 block" onClick={removeModel}>Choose another model</Button>
              </div>}

              {/* TL overlay: reference toggles */}
              <div className="absolute top-4 left-4 flex flex-col gap-1.5 max-w-[220px]" data-testid="quote-ref-toggles">
                <div className="font-mono text-[9px] uppercase tracking-[0.28em] text-white/50 pl-1 mb-0.5">Scale refs</div>
                {[
                  { key: "quarter", label: "Quarter", hint: "24×2mm",   swatch: "#c8a961" },
                  { key: "can",     label: "Soda Can", hint: "66×122mm", swatch: "#d94848" },
                  { key: "bottle",  label: "2L Bottle", hint: "110×330mm", swatch: "#60a5fa" },
                ].map((r) => (
                  <button
                    key={r.key}
                    type="button"
                    data-testid={`ref-toggle-${r.key}`}
                    onClick={() => setRefs((s) => ({ ...s, [r.key]: !s[r.key] }))}
                    className={`inline-flex items-center gap-2 rounded-full border pl-2 pr-3 py-1 font-mono text-[10px] uppercase tracking-[0.16em] transition-colors backdrop-blur-sm ${
                      refs[r.key]
                        ? "border-white/40 bg-black/60 text-white"
                        : "border-white/10 bg-black/40 text-white/45 hover:text-white hover:border-white/25"
                    }`}
                  >
                    <span className="h-2 w-2 rounded-full" style={{ background: r.swatch, opacity: refs[r.key] ? 1 : 0.35 }} />
                    <span>{r.label}</span>
                    <span className="text-white/40 text-[9px] tracking-normal ml-auto">{r.hint}</span>
                  </button>
                ))}
              </div>

              {/* TR overlay: manual-review badge */}
              {bedIssue && (
                <div className="absolute right-4 top-4 max-w-xs rounded-xl border border-amber-400/50 bg-amber-500/15 backdrop-blur-md px-3 py-2 text-[11px] text-amber-100" data-testid="quote-card-warning">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5 text-amber-300" />
                    <div className="leading-snug">
                      <div className="font-mono uppercase tracking-[0.18em] text-[9px] font-bold text-amber-200">Manual review</div>
                      <div className="mt-0.5 font-mono text-[10px] text-amber-100/80">{bedIssue}</div>
                    </div>
                  </div>
                </div>
              )}

              {/* BL overlay: current print size */}
              {scaledDims && (
                <div className="absolute bottom-4 left-4 rounded-xl border border-white/10 bg-black/60 backdrop-blur-md px-3.5 py-2.5" data-testid="quote-dim-readout">
                  <div className="flex items-center gap-2 mb-1">
                    <Ruler className="h-3 w-3 text-fuchsia-300" />
                    <span className="font-mono text-[9px] uppercase tracking-[0.26em] text-fuchsia-300/90">Current print size</span>
                  </div>
                  <div className="font-mono text-white tabular-nums text-[13px]">
                    {scaledDims.x.toFixed(1)} × {scaledDims.y.toFixed(1)} × {scaledDims.z.toFixed(1)} <span className="text-white/40">mm</span>
                  </div>
                  <div className="font-mono text-white/50 tabular-nums text-[10px] mt-0.5">
                    {mmToIn(scaledDims.x).toFixed(2)} × {mmToIn(scaledDims.y).toFixed(2)} × {mmToIn(scaledDims.z).toFixed(2)} in
                    <span className="text-white/25 ml-2">· Bed {config?.bed_mm?.x}×{config?.bed_mm?.y}×{config?.bed_mm?.z}</span>
                  </div>
                </div>
              )}

              {/* BR overlay: upload progress */}
              {uploading && (
                <div className="absolute bottom-4 right-4 rounded-xl border border-fuchsia-300/40 bg-black/60 backdrop-blur-md px-3 py-2 text-[11px] text-fuchsia-200">
                  <div className="flex items-center gap-2 font-mono uppercase tracking-[0.18em] text-[10px]">
                    <Upload className="h-3 w-3" /> Uploading {uploadPct}%
                  </div>
                  <div className="mt-1.5 h-1 w-40 rounded-full bg-white/5 overflow-hidden">
                    <div className="h-full bg-fuchsia-400 transition-all" style={{ width: `${uploadPct}%` }} />
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* RIGHT — control rail (scrollable if content overflows) */}
        <aside className="bg-[hsl(222_22%_6%)]/95 lg:overflow-y-auto lg:h-[calc(100vh-3.5rem)]">
          <div className="p-6 sm:p-7 space-y-6">
            {/* Live estimate hero */}
            <div className="rounded-2xl border border-fuchsia-300/25 bg-gradient-to-br from-fuchsia-500/[0.08] to-black/40 p-6">
              <div className="flex items-center gap-2 mb-4">
                <Coins className="h-3.5 w-3.5 text-fuchsia-300" />
                <span className="font-mono text-[9px] uppercase tracking-[0.28em] text-fuchsia-300">Live estimate</span>
              </div>
              {quote ? (
                <>
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <div className="font-display text-[42px] leading-none tracking-[0.04em] text-white tabular-nums" data-testid="quote-live-total">${quote.total.toFixed(2)}</div>
                    {quote.quantity > 1 && (
                      <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-white/50">{quote.quantity} units · ${quote.unit_price.toFixed(2)}/ea</div>
                    )}
                  </div>
                  <div className="mt-5 space-y-2 text-[13px]">
                    <QuoteLine label={`Resin · ${quote.weight_g.toFixed(1)} g`} value={`$${quote.material_total.toFixed(2)}`} />
                    <QuoteLine label={`Print · ${fmtHours(quote.print_hours)}${quote.quantity > 1 ? ` × ${quote.quantity}` : ""}`} value={`$${quote.time_total.toFixed(2)}`} />
                    <QuoteLine label={`Curing & clean · ${fmtHours(quote.print_hours)}${quote.quantity > 1 ? ` × ${quote.quantity}` : ""}`} value={`$${quote.curing_total.toFixed(2)}`} />
                  </div>
                  {/* Shipping-not-included disclosure — separate row so it
                      can never be missed. Amber pill matches "manual review"
                      language without alarm-red. */}
                  <div className="mt-4 rounded-xl border border-amber-400/30 bg-amber-500/[0.06] px-3 py-2.5" data-testid="quote-shipping-note">
                    <div className="flex items-start gap-2 text-[11px] leading-snug text-amber-100/90">
                      <Package className="h-3.5 w-3.5 shrink-0 mt-0.5 text-amber-300" />
                      <div>
                        <span className="font-mono uppercase tracking-[0.16em] text-[9px] font-semibold text-amber-200">Shipping not included</span>
                        <div className="mt-0.5 text-amber-100/80">
                          Primary delivery via <span className="font-semibold text-amber-100">Whatnot</span> — very cost-effective. Direct shipping (USPS/UPS) quoted separately after approval; can be $35+ where Whatnot is ~$8.
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-sm text-white/50 py-3">
                  {file ? "Measuring model…" : "Drop an STL or OBJ to see your estimate."}
                </div>
              )}
            </div>

            {/* Scale controls */}
            <div className="rounded-2xl border border-white/10 bg-black/30 p-5 space-y-5">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <div className="font-mono text-[9px] uppercase tracking-[0.28em] text-white/60">Base scale</div>
                  <div className="font-display text-2xl tracking-[0.05em] text-white tabular-nums mt-1">
                    {(baseScale * 100).toFixed(0)}<span className="text-white/40 text-base">%</span>
                    {Math.abs(jog) > 0.001 && (
                      <>
                        <span className="text-white/30 text-sm mx-2">→</span>
                        <span className="text-cyan-300">{(effectiveScale * 100).toFixed(0)}<span className="text-cyan-300/50 text-base">%</span></span>
                      </>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  data-testid="quote-scale-fit"
                  disabled={!effectiveBbox}
                  onClick={() => {
                    if (!effectiveBbox || !config?.bed_mm) return;
                    const bed = config.bed_mm;
                    const fit = Math.min(bed.x / effectiveBbox.x, bed.y / effectiveBbox.y, bed.z / effectiveBbox.z) * 0.98;
                    commitBase(fit);
                  }}
                  className="rounded-lg border border-fuchsia-300/40 bg-fuchsia-400/10 hover:bg-fuchsia-400/20 px-3.5 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-fuchsia-200 disabled:opacity-40 transition-colors inline-flex items-center gap-1.5"
                >
                  <Maximize2 className="h-3 w-3" /> Fill bed
                </button>
              </div>
              <div className="flex items-center gap-3">
                <input
                  data-testid="quote-scale-slider"
                  type="range"
                  min={-1}
                  max={1}
                  step={0.005}
                  value={jog}
                  onChange={(e) => setJog(parseFloat(e.target.value))}
                  onPointerDown={beginJog}
                  onMouseDown={beginJog}
                  onTouchStart={beginJog}
                  onKeyDown={beginJog}
                  className="flex-1 accent-fuchsia-400"
                />
                <Input
                  data-testid="quote-scale-number"
                  type="number"
                  step="0.05"
                  value={baseScale}
                  onChange={(e) => {
                    const v = parseFloat(e.target.value);
                    if (!isNaN(v) && v > 0) commitBase(v);
                  }}
                  className="w-20 h-9 bg-white/5 border-white/10 text-white text-center tabular-nums text-[13px] px-1"
                />
              </div>
              <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/40 text-center">
                Nudge left/right · releases into base
              </div>

              {/* Target dims inline — compact 3-input row */}
              <TargetDimensions
                bbox={effectiveBbox}
                scale={effectiveScale}
                disabled={!geometry?.volume_mm3 && !parseFloat(manualVolume || 0)}
                onChange={commitBase}
              />
            </div>

            {/* Meta: qty + notes (collapsible) */}
            <Collapsible className="rounded-2xl border border-white/10 bg-black/30" data-testid="quote-meta-collapsible">
              <div className="p-5 flex items-center gap-4">
                <div className="w-20 shrink-0">
                  <Label className="font-mono text-[9px] uppercase tracking-[0.28em] text-white/60 mb-1 inline-block">Qty</Label>
                  <Input
                    data-testid="quote-quantity"
                    type="number"
                    step="1"
                    min="1"
                    value={quantity}
                    onChange={(e) => setQuantity(Math.max(1, Math.min(100, parseInt(e.target.value || "1", 10))))}
                    className="h-9 bg-white/5 border-white/10 text-white text-center tabular-nums text-[13px]"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <Label className="font-mono text-[9px] uppercase tracking-[0.28em] text-white/60 mb-1 inline-block">Material</Label>
                  <div className="h-9 rounded-md border border-white/10 bg-white/5 px-3 flex items-center text-[12px] text-white/80 truncate" data-testid="quote-material">
                    {config?.materials?.[0]?.label?.split("(")[0].trim() || "Elegoo Standard Resin"}
                  </div>
                </div>
                <CollapsibleTrigger asChild>
                  <button
                    type="button"
                    data-testid="quote-meta-toggle"
                    className="group h-9 w-9 rounded-md border border-white/10 bg-white/5 hover:bg-white/10 flex items-center justify-center text-white/60 shrink-0 mt-5"
                    title="Add notes"
                  >
                    <ChevronRight className="h-4 w-4 transition-transform group-data-[state=open]:rotate-90" />
                  </button>
                </CollapsibleTrigger>
              </div>
              <CollapsibleContent>
                <div className="px-5 pb-5 pt-1">
                  <Label className="font-mono text-[9px] uppercase tracking-[0.28em] text-white/60 mb-1.5 inline-block">Notes for the printer</Label>
                  <Textarea
                    data-testid="quote-notes"
                    placeholder="e.g. hollow it out, ship raw (unpainted), add supports on the wings…"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    className="bg-white/5 border-white/10 text-white placeholder:text-white/30 min-h-[80px] text-[12px]"
                  />
                </div>
              </CollapsibleContent>
            </Collapsible>

            {/* Manual dims fallback (only when viewer couldn't measure) */}
            {file && !geometry?.volume_mm3 && (
              <div className="rounded-2xl border border-amber-400/25 bg-amber-500/5 p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Ruler className="h-3.5 w-3.5 text-amber-300" />
                  <span className="font-mono text-[9px] uppercase tracking-[0.24em] text-amber-200">Manual dims (file too big to preview)</span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <NumField label="Volume cm³" testid="manual-volume-cm3" value={manualVolume} onChange={setManualVolume} placeholder="45" compact />
                  <NumField label="Bbox X mm" value={manualBbox.x} onChange={(v) => setManualBbox({ ...manualBbox, x: parseFloat(v || 0) })} compact />
                  <NumField label="Bbox Y mm" value={manualBbox.y} onChange={(v) => setManualBbox({ ...manualBbox, y: parseFloat(v || 0) })} compact />
                  <NumField label="Bbox Z mm" value={manualBbox.z} onChange={(v) => setManualBbox({ ...manualBbox, z: parseFloat(v || 0) })} compact />
                </div>
                <p className="text-[10px] text-white/45 mt-2">Open your file in ChiTuBox / Lychee — grab volume + bounding box from there.</p>
              </div>
            )}

            {/* CTA */}
            <Button
              data-testid="quote-continue"
              disabled={!quote || !(uploaded || oversize) || catalog.loading || !!catalog.error || !!modelError}
              onClick={() => setShowContact(true)}
              className="w-full h-11 bg-fuchsia-400 text-black hover:bg-fuchsia-300 disabled:opacity-40 font-medium"
            >
              Continue to contact &amp; shipping <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
            {oversize && (
              <div className="rounded-xl border border-amber-400/40 bg-amber-400/10 p-3 text-[11px] text-amber-100" data-testid="quote-oversize-hint">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                  <div>
                    <div className="font-medium">File over the {config?.file_upload_limit_mb} MB limit.</div>
                    <div className="mt-1 text-amber-100/80">Submit anyway, then email your file to <span className="font-mono text-white">admin@playden.studio</span> with the Quote ID.</div>
                  </div>
                </div>
              </div>
            )}
            <p className="text-[10px] text-white/40 leading-relaxed" data-testid="quote-disclaimer">
              {config?.disclaimer || "This is an estimate. Real price may differ once the admin reviews. Payment via Venmo after approval."}
            </p>
          </div>
        </aside>
      </div>

      {/* Contact + shipping drawer */}
      <Sheet open={showContact} onOpenChange={setShowContact}>
        <SheetContent side="right" className="w-full sm:max-w-[520px] bg-[hsl(222_22%_6%)] border-white/10 text-white overflow-y-auto" data-testid="quote-contact-sheet">
          <SheetHeader className="text-left">
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-fuchsia-300" />
              <SheetTitle className="font-display text-xl tracking-[0.05em] text-white">CONTACT &amp; SHIPPING</SheetTitle>
            </div>
            <SheetDescription className="text-white/50 text-sm">
              We need this to ship the print once approved. Nothing is charged until you sign off on the final quote.
            </SheetDescription>
          </SheetHeader>
          {/* Shipping-cost disclosure inside the drawer where the user is
              literally typing an address — impossible to miss now. */}
          <div className="mt-4 rounded-xl border border-amber-400/40 bg-amber-500/[0.08] p-3.5" data-testid="quote-shipping-note-drawer">
            <div className="flex items-start gap-2.5">
              <Package className="h-4 w-4 shrink-0 mt-0.5 text-amber-300" />
              <div className="text-[12px] leading-relaxed text-amber-100/95">
                <div className="font-mono uppercase tracking-[0.16em] text-[10px] font-semibold text-amber-200 mb-1">Shipping billed separately</div>
                The estimate above covers the print only. Our primary delivery method is <span className="font-semibold">Whatnot</span> — the live-selling app — which keeps shipping extremely cost-effective. Direct USPS/UPS is available on request but tends to run ~$35 where Whatnot moves the same box for ~$8. We&apos;ll confirm the exact shipping cost after we review your file.
              </div>
            </div>
          </div>
          <div className="mt-6 space-y-5">
            <div className="grid sm:grid-cols-2 gap-4">
              <TextField label="Full name" testid="quote-name" value={contact.name} onChange={(v) => setContact({ ...contact, name: v })} />
              <TextField label="Email" testid="quote-email" type="email" value={contact.email} onChange={(v) => setContact({ ...contact, email: v })} />
              <TextField label="Phone" testid="quote-phone" type="tel" value={contact.phone} onChange={(v) => setContact({ ...contact, phone: v })} />
            </div>
            <div className="border-t border-white/10 pt-4">
              <div className="font-mono text-[9px] uppercase tracking-[0.28em] text-white/50 mb-3">Ship to</div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <TextField label="Address line 1" testid="quote-ship-l1" value={ship.line1} onChange={(v) => setShip({ ...ship, line1: v })} />
                </div>
                <div className="sm:col-span-2">
                  <TextField label="Line 2 (optional)" testid="quote-ship-l2" value={ship.line2} onChange={(v) => setShip({ ...ship, line2: v })} />
                </div>
                <TextField label="City" testid="quote-ship-city" value={ship.city} onChange={(v) => setShip({ ...ship, city: v })} />
                <TextField label="State / Region" testid="quote-ship-region" value={ship.region} onChange={(v) => setShip({ ...ship, region: v })} />
                <TextField label="Postal code" testid="quote-ship-postal" value={ship.postal_code} onChange={(v) => setShip({ ...ship, postal_code: v })} />
                <TextField label="Country" testid="quote-ship-country" value={ship.country} onChange={(v) => setShip({ ...ship, country: v })} />
              </div>
            </div>
          </div>
          <SheetFooter className="mt-6 flex-col sm:flex-col gap-2">
            <div className="w-full flex items-center justify-between rounded-xl border border-fuchsia-300/25 bg-fuchsia-500/[0.06] px-4 py-3">
              <div>
                <div className="font-mono text-[9px] uppercase tracking-[0.28em] text-fuchsia-300/70">Estimate</div>
                <div className="font-display text-2xl tracking-[0.05em] text-white tabular-nums">${(quote?.total || 0).toFixed(2)}</div>
              </div>
              <div className="text-right">
                <div className="font-mono text-[9px] uppercase tracking-[0.28em] text-white/50">Print time</div>
                <div className="font-mono text-sm text-white/80 tabular-nums">{quote ? fmtHours(quote.print_hours) : "—"}</div>
              </div>
            </div>
            <Button
              data-testid="quote-submit"
              onClick={submit}
              disabled={!canSubmit}
              className="w-full h-11 bg-fuchsia-400 text-black hover:bg-fuchsia-300 disabled:opacity-40 font-medium"
            >
              <Send className="h-4 w-4 mr-1.5" />
              {submitting ? "Submitting…" : "Submit for approval"}
            </Button>
            {!canSubmit && !submitting && (
              <div className="text-[10px] text-white/45 font-mono uppercase tracking-[0.15em] text-center">
                Complete every field to enable submit
              </div>
            )}
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </div>
  );
}

// ── Dropzone ─────────────────────────────────────────────────────
function Dropzone({ file, uploading, uploadPct, uploaded, oversize, limitMb, onFile }) {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) onFile(f);
  };
  return (
    <div
      onDrop={onDrop}
      onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onClick={() => inputRef.current?.click()}
      data-testid="quote-dropzone"
      className={`rounded-2xl border-2 border-dashed cursor-pointer p-6 transition-colors ${dragging ? "border-fuchsia-300 bg-fuchsia-400/5" : "border-white/15 hover:border-white/30 bg-[hsl(222_22%_7%)]/60"}`}
    >
      <input
        ref={inputRef}
        type="file"
        accept=".stl,.obj"
        className="hidden"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(f); }}
        data-testid="quote-file-input"
      />
      <div className="flex items-center gap-4">
        <div className="h-12 w-12 rounded-xl bg-fuchsia-400/10 border border-fuchsia-300/40 flex items-center justify-center shrink-0">
          <Upload className="h-6 w-6 text-fuchsia-300" />
        </div>
        <div className="flex-1 min-w-0">
          {!file ? (
            <>
              <div className="text-white font-medium">Drop your model here, or click to browse</div>
              <div className="text-xs text-white/50 mt-0.5">STL or OBJ — up to {limitMb} MB (larger files: send via Dropbox)</div>
            </>
          ) : (
            <>
              <div className="text-white font-medium truncate">{file.name}</div>
              <div className="text-xs text-white/50 mt-0.5">
                {(file.size / 1024 / 1024).toFixed(1)} MB
                {oversize && <span className="text-amber-300 ml-2">· OVERSIZE — Dropbox required</span>}
                {uploading && <span className="text-fuchsia-300 ml-2">· Uploading {uploadPct}%</span>}
                {!uploading && uploaded && <span className="text-emerald-300 ml-2">· Uploaded</span>}
              </div>
            </>
          )}
        </div>
      </div>
      {uploading && (
        <div className="mt-3 h-1.5 rounded-full bg-white/5 overflow-hidden">
          <div className="h-full bg-fuchsia-400 transition-all" style={{ width: `${uploadPct}%` }} />
        </div>
      )}
    </div>
  );
}

function TextField({ label, value, onChange, testid, type = "text" }) {
  return (
    <div>
      <Label className="font-mono text-[10px] uppercase tracking-[0.28em] text-white/70 mb-1 inline-block">{label}</Label>
      <Input
        data-testid={testid}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
      />
    </div>
  );
}
function NumField({ label, value, onChange, testid, placeholder, compact = false }) {
  return (
    <div>
      <Label className={`font-mono uppercase tracking-[0.28em] text-white/60 mb-1 inline-block ${compact ? "text-[9px]" : "text-[10px]"} ${compact ? "text-white/60" : "text-white/70"}`}>{label}</Label>
      <Input
        data-testid={testid}
        type="number"
        step="any"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={`bg-white/5 border-white/10 text-white placeholder:text-white/30 ${compact ? "h-9 text-[13px] w-16" : ""}`}
      />
    </div>
  );
}

/** Full-viewport dropzone — replaces the empty viewer stage when no file
 *  is loaded. Big, unmistakable target that responds to drag-over. */
function FullscreenDropzone({ limitMb, onFile }) {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) onFile(f);
  };
  return (
    <div
      onDrop={onDrop}
      onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onClick={() => inputRef.current?.click()}
      data-testid="quote-dropzone"
      className={`h-full min-h-[560px] lg:min-h-0 flex items-center justify-center cursor-pointer transition-colors ${dragging ? "bg-fuchsia-400/[0.06]" : "bg-transparent hover:bg-white/[0.02]"}`}
    >
      <input
        ref={inputRef}
        type="file"
        accept=".stl,.obj"
        className="hidden"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(f); }}
        data-testid="quote-file-input"
      />
      <div className={`text-center transition-transform ${dragging ? "scale-[1.02]" : ""}`}>
        <div className={`mx-auto h-20 w-20 rounded-2xl border-2 border-dashed flex items-center justify-center transition-colors ${dragging ? "border-fuchsia-300 bg-fuchsia-400/10" : "border-white/20"}`}>
          <Upload className={`h-9 w-9 transition-colors ${dragging ? "text-fuchsia-300" : "text-white/50"}`} />
        </div>
        <div className="mt-6 font-display text-2xl sm:text-3xl tracking-[0.06em] text-white">DROP YOUR MODEL</div>
        <div className="mt-2 font-mono text-[11px] uppercase tracking-[0.28em] text-white/50">or click to browse</div>
        <div className="mt-6 flex items-center justify-center gap-3 font-mono text-[10px] uppercase tracking-[0.22em] text-white/40">
          <span>STL · OBJ</span>
          <span className="h-1 w-1 rounded-full bg-white/30" />
          <span>up to {limitMb} MB</span>
          <span className="h-1 w-1 rounded-full bg-white/30" />
          <span>Larger? Dropbox link works</span>
        </div>
      </div>
    </div>
  );
}

/** Auto-scale controls — user types a target dimension in mm and we solve
 *  back to a uniform scale factor. Whenever any axis is edited, all three
 *  axes rescale proportionally because the model can only be scaled
 *  uniformly (asymmetric scaling would distort the print). */
function TargetDimensions({ bbox, scale, disabled, onChange }) {
  // Current on-screen dimensions at the current scale.
  const cx = (bbox?.x || 0) * scale;
  const cy = (bbox?.y || 0) * scale;
  const cz = (bbox?.z || 0) * scale;

  // Local buffers so users can freely edit; the useEffect below re-syncs
  // them any time the actual scaled dims change from OUTSIDE this component
  // (fit-to-bed, slider, etc.) — but never mid-typing.
  const [dx, setDx] = useState("");
  const [dy, setDy] = useState("");
  const [dz, setDz] = useState("");
  useEffect(() => {
    setDx(cx ? cx.toFixed(1) : "");
    setDy(cy ? cy.toFixed(1) : "");
    setDz(cz ? cz.toFixed(1) : "");
  }, [cx, cy, cz]);

  // commit reads the value straight from the DOM event so there's no chance
  // of a stale-closure bug over the local buffer state. No clamp here — the
  // upstream `commitBase` does the single, authoritative clamp so users
  // never see their typed number get silently snapped to a "preset".
  const commit = (axis, raw) => {
    const target = parseFloat(raw);
    const source = axis === "x" ? bbox?.x : axis === "y" ? bbox?.y : bbox?.z;
    if (!Number.isFinite(target) || target <= 0 || !source || source <= 0) return;
    onChange(target / source);
  };

  return (
    <div className="pt-3" data-testid="quote-target-dimensions">
      <div className="flex items-center justify-between mb-2.5 gap-3">
        <Label className="font-mono text-[10px] uppercase tracking-[0.24em] text-white/70">
          Target size
        </Label>
        <span className="font-mono text-[9px] text-white/40 uppercase tracking-[0.22em]">Type any · uniform scale</span>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <TargetAxis axis="X" testid="target-dim-x" value={dx} setValue={setDx} onCommit={(v) => commit("x", v)} disabled={disabled} />
        <TargetAxis axis="Y" testid="target-dim-y" value={dy} setValue={setDy} onCommit={(v) => commit("y", v)} disabled={disabled} />
        <TargetAxis axis="Z" testid="target-dim-z" value={dz} setValue={setDz} onCommit={(v) => commit("z", v)} disabled={disabled} />
      </div>
    </div>
  );
}

function TargetAxis({ axis, testid, value, setValue, onCommit, disabled }) {
  const inValue = mmToIn(parseFloat(value || 0));
  return (
    <div>
      <div className="text-[10px] font-mono uppercase tracking-[0.24em] text-white/50 mb-1">{axis} · mm</div>
      <div className="relative">
        <Input
          data-testid={testid}
          type="number"
          step="0.1"
          min={1}
          value={value}
          disabled={disabled}
          onChange={(e) => setValue(e.target.value)}
          onBlur={(e) => onCommit(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              onCommit(e.currentTarget.value);
              e.currentTarget.blur();
            }
          }}
          className="bg-white/5 border-white/10 text-white pr-14 tabular-nums"
        />
        <span className="absolute right-2 top-1/2 -translate-y-1/2 font-mono text-[10px] text-white/40 tabular-nums pointer-events-none">
          {inValue ? `${inValue.toFixed(2)}"` : ""}
        </span>
      </div>
    </div>
  );
}

function QuoteLine({ label, value, hint }) {
  return (
    <div className="flex items-baseline justify-between text-sm">
      <span className="text-white/55">
        {label}
        {hint && <span className="text-white/30 text-[10px] font-mono ml-1.5">· {hint}</span>}
      </span>
      <span className="text-white font-mono tabular-nums">{value}</span>
    </div>
  );
}
function Metric({ label, value, accent, testid }) {
  return (
    <div className="rounded-xl border p-4" style={{ borderColor: `${accent}55`, background: `${accent}10` }} data-testid={testid}>
      <div className="font-mono text-[10px] uppercase tracking-[0.28em]" style={{ color: accent }}>{label}</div>
      <div className="mt-1 font-display text-2xl text-white tracking-[0.03em]">{value}</div>
    </div>
  );
}
